/**
 * CalendarEditable Component
 * 
 * A fully-featured calendar component for displaying and managing court events.
 * 
 * Features:
 * - Displays court events from the CourtEvent table
 * - Uses react-big-calendar with luxon for date handling
 * - Multiple calendar views (Month, Week, Day, Agenda)
 * - Click events to show detailed modals
 * - Event modal displays both CourtEvent and associated Ticket details
 * - Save and Delete functionality for events
 * 
 * @param props.courtEvents - Array of CourtEventDto objects to display on the calendar
 * @param props.onSave - Callback function called when user saves an event (can be sync or async)
 * @param props.onDelete - Callback function called when user deletes an event (can be sync or async)
 * @param props.onRangeChange - Optional callback function called when user navigates or changes view (date, view)
 * 
 * Usage Example:
 * ```tsx
 * const [events, setEvents] = useState<CourtEventDto[]>([]);
 * 
 * const handleSave = async (event: CourtEventDto) => {
 *   // Handle save logic (can be async)
 *   const success = await saveEventToAPI(event);
 *   return success; // Return true if save was successful
 * };
 * 
 * const handleDelete = async (event: CourtEventDto) => {
 *   // Handle delete logic (can be async)
 *   const success = await deleteEventFromAPI(event);
 *   return success; // Return true if delete was successful
 * };
 * 
 * const handleRangeChange = (date: Date, view: View) => {
 *   // Handle date range changes for loading new events
 *   console.log('New date range:', date, 'View:', view);
 * };
 * 
 * <CalendarEditable 
 *   courtEvents={events}
 *   onSave={handleSave}
 *   onDelete={handleDelete}
 *   onRangeChange={handleRangeChange}
 * />
 * ```
 */

import React, { useState, useMemo } from "react";
import { Calendar, luxonLocalizer, Views, Event, View } from 'react-big-calendar';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import { Edit, Delete, Close, Visibility } from '@mui/icons-material';
import { CourtEventDto, FullCourtEventDto, UpdateCourtEventDto, TicketDto, UserDto } from "../../api/CourtEventApi";
import CourtEventApi from "../../api/CourtEventApi";
import AppearanceControl from "../AppearanceControl/AppearanceControl";
import './CalendarEditable.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { courtEventColor } from '../../utils/courtEventColor';

// Setup the localizer for react-big-calendar using luxon
const localizer = luxonLocalizer(DateTime);

// Extended event interface for the calendar
interface CalendarEvent extends Event {
  courtEvent: CourtEventDto;
  resource?: any;
}

const CalendarEditable = (props: {
  courtEvents: CourtEventDto[]
  onSave: (event: CourtEventDto) => Promise<boolean> | boolean;
  onDelete: (event: CourtEventDto) => Promise<boolean> | boolean;
  onRangeChange?: (date: Date, view: View) => void;
}) => {
  const [selectedEvent, setSelectedEvent] = useState<CourtEventDto | null>(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fullEventData, setFullEventData] = useState<FullCourtEventDto | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    CourtEvent_StartDate: '',
    CourtEvent_StartTime: '',
    CourtEvent_EndDate: '',
    CourtEvent_EndTime: '',
    CourtEvent_Title: '',
    CourtEvent_Desc: '',
  });
  const [currentView, setCurrentView] = useState<View>(Views.MONTH);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Handlers for calendar navigation and view changes
  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
    if (props.onRangeChange) {
      props.onRangeChange(date, currentView);
    }
  };

  const handleViewChange = (view: View) => {
    setCurrentView(view);
    if (props.onRangeChange) {
      // Use the current date being displayed on the calendar
      props.onRangeChange(currentDate, view);
    }
  };

  // Function to check if the court event form is complete
  // Description is deliberately not required — a court date often has nothing to add beyond the
  // date, time and title. Mirrors courtEventFormProblem in ManagerUserView / ManagerTicketView.
  const isCourtEventFormComplete = () => {
    return editForm.CourtEvent_StartDate.trim() !== '' &&
           editForm.CourtEvent_StartTime.trim() !== '' &&
           editForm.CourtEvent_EndDate.trim() !== '' &&
           editForm.CourtEvent_EndTime.trim() !== '' &&
           editForm.CourtEvent_Title.trim() !== '';
  };

  // Function to check if end date/time is after start date/time
  const isEndTimeAfterStartTime = () => {
    if (!editForm.CourtEvent_StartDate || !editForm.CourtEvent_StartTime || 
        !editForm.CourtEvent_EndDate || !editForm.CourtEvent_EndTime) {
      return true; // Allow if fields are empty (will be caught by other validation)
    }

    const startDateTime = new Date(`${editForm.CourtEvent_StartDate}T${editForm.CourtEvent_StartTime}`);
    const endDateTime = new Date(`${editForm.CourtEvent_EndDate}T${editForm.CourtEvent_EndTime}`);
    
    // End time must be strictly after start time (not equal)
    return endDateTime > startDateTime;
  };

  // Function to check if the entire form is valid
  const isCourtEventFormValid = () => {
    return isCourtEventFormComplete() && isEndTimeAfterStartTime();
  };

  // Format event title for display on calendar.
  // No time prefix: react-big-calendar already renders its own "5:00 AM – 5:30 AM" label above the
  // title in week/day view, so prefixing the time printed it twice and pushed the case name out of
  // the visible box.
  const formatEventTitle = (courtEvent: CourtEventDto) => {
    return courtEvent.CourtEvent_Title || 'Court Event';
  };

  // Transform court events into calendar events
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    return props.courtEvents.map(courtEvent => {
      const startDate = DateTime.fromMillis(parseInt('' + courtEvent.CourtEvent_Start));
      const endDate = DateTime.fromMillis(parseInt('' + courtEvent.CourtEvent_End));

      return {
        id: courtEvent.CourtEvent_guid,
        title: formatEventTitle(courtEvent),
        start: startDate.toJSDate(),
        end: endDate.toJSDate(),
        courtEvent: courtEvent,
        resource: courtEvent
      };
    });
  }, [props.courtEvents]);

  const handleSelectEvent = async (event: CalendarEvent) => {
    setSelectedEvent(event.courtEvent);
    setEventModalOpen(true);
    setLoading(true);
    setError(null);
    setFullEventData(null);

    try {
      // Fetch the full event data from the API
      const fullEventData = await CourtEventApi.getCourtEvent(event.courtEvent.CourtEvent_guid);
      setFullEventData(fullEventData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setEventModalOpen(false);
    setSelectedEvent(null);
    setFullEventData(null);
    setError(null);
    setSaving(false);
    setIsEditing(false);
    setEditForm({
      CourtEvent_StartDate: '',
      CourtEvent_StartTime: '',
      CourtEvent_EndDate: '',
      CourtEvent_EndTime: '',
      CourtEvent_Title: '',
      CourtEvent_Desc: '',
    });
  };

  const handleEditEvent = () => {
    if (!selectedEvent) return;
    
    // Convert epoch time back to date/time strings for form inputs
    const startDate = new Date(parseInt('' + selectedEvent.CourtEvent_Start));
    const endDate = new Date(parseInt('' + selectedEvent.CourtEvent_End));
    
    setEditForm({
      CourtEvent_StartDate: startDate.toISOString().split('T')[0], // YYYY-MM-DD
      CourtEvent_StartTime: startDate.toTimeString().split(' ')[0].slice(0, 5), // HH:MM
      CourtEvent_EndDate: endDate.toISOString().split('T')[0], // YYYY-MM-DD
      CourtEvent_EndTime: endDate.toTimeString().split(' ')[0].slice(0, 5), // HH:MM
      CourtEvent_Title: selectedEvent.CourtEvent_Title,
      CourtEvent_Desc: selectedEvent.CourtEvent_Desc,
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      CourtEvent_StartDate: '',
      CourtEvent_StartTime: '',
      CourtEvent_EndDate: '',
      CourtEvent_EndTime: '',
      CourtEvent_Title: '',
      CourtEvent_Desc: '',
    });
  };

  const handleSaveEvent = async () => {
    if (!selectedEvent || !isEditing) return;
    
    // Additional validation before submission
    if (!isCourtEventFormValid()) {
      setError('Please ensure all fields are filled correctly and end time is after start time.');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Convert form date and time to epoch timestamps
      const startDateTimeString = `${editForm.CourtEvent_StartDate}T${editForm.CourtEvent_StartTime}`;
      const endDateTimeString = `${editForm.CourtEvent_EndDate}T${editForm.CourtEvent_EndTime}`;
      const startEpoch = new Date(startDateTimeString).getTime();
      const endEpoch = new Date(endDateTimeString).getTime();

      // Double-check that end time is after start time
      if (endEpoch <= startEpoch) {
        setError('End date and time must be after start date and time.');
        return;
      }

      // Create updated event object
      const updatedEvent: CourtEventDto = {
        ...selectedEvent,
        CourtEvent_Start: startEpoch,
        CourtEvent_End: endEpoch,
        CourtEvent_Title: editForm.CourtEvent_Title,
        CourtEvent_Desc: editForm.CourtEvent_Desc,
      };

      const result = await Promise.resolve(props.onSave(updatedEvent));
      if (result) {
        setSelectedEvent(updatedEvent);
        setIsEditing(false);
        // Update full event data if it exists
        if (fullEventData) {
          setFullEventData({
            ...fullEventData,
            courtEvent: updatedEvent
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      setSaving(true);
      const result = await Promise.resolve(props.onDelete(selectedEvent));
      if (result) {
        handleCloseModal();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete event');
    } finally {
      setSaving(false);
      setDeleteConfirmOpen(false);
    }
  };

  const formatDateTime = (timestamp: number) => {
    return DateTime.fromMillis(parseInt('' + timestamp)).toLocaleString(DateTime.DATETIME_MED);
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    // No opacity: 0.8 washed white text out against the fill and cost real contrast.
    const style = {
      backgroundColor: courtEventColor(event.courtEvent.CourtEvent_Title),
      borderRadius: '4px',
      color: 'white',
      border: '0px',
      display: 'block',
      padding: '3px 6px'
    };
    return { style };
  };

  /** Native tooltip so a clipped title is still readable on hover, without opening the modal. */
  const eventTooltip = (event: CalendarEvent) => {
    const start = DateTime.fromMillis(parseInt('' + event.courtEvent.CourtEvent_Start));
    return `${start.toFormat('ccc d LLL, h:mm a')} — ${event.courtEvent.CourtEvent_Title || 'Court Event'}`;
  };

  return (
    <Box className="calendar-editable">
      <Box className="calendar-container" sx={{ height: 820 }}>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          defaultView={Views.MONTH}
          view={currentView}
          date={currentDate}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          tooltipAccessor={eventTooltip}
          popup
          showMultiDayTimes
          // One 30-minute slot per labelled hour row rather than two: with two, a 30-minute hearing
          // got roughly 20px of height and the title was sliced through the middle of the letters.
          step={30}
          timeslots={1}
          // Concurrent hearings sit side by side instead of overlapping, so a court date behind
          // another one is still legible rather than half-hidden.
          dayLayoutAlgorithm="no-overlap"
          className="custom-calendar"
        />
      </Box>

      {/* Event Details Modal */}
      <Dialog 
        open={eventModalOpen} 
        onClose={handleCloseModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '400px' }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              Court Event Details
            </Typography>
            
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {selectedEvent && !loading && (
            <Box>
              {isEditing ? (
                /* Edit Form */
                <Stack spacing={3}>
                  <Typography variant="h6" color="primary">
                    Edit Event Information
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" color="primary">
                      Start Date & Time
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Start Date"
                        type="date"
                        value={editForm.CourtEvent_StartDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          const newStartTime = editForm.CourtEvent_StartTime;
                          
                          let newEndDate = newStartDate;
                          let newEndTime = editForm.CourtEvent_EndTime;
                          
                          // If we have both start date and start time, calculate end time as 30 minutes later
                          if (newStartDate && newStartTime) {
                            const [hours, minutes] = newStartTime.split(':');
                            const startDateTime = new Date(`${newStartDate}T${newStartTime}`);
                            const endDateTime = new Date(startDateTime.getTime() + 30 * 60000); // Add 30 minutes
                            
                            newEndDate = endDateTime.toISOString().split('T')[0];
                            newEndTime = endDateTime.toTimeString().split(' ')[0].slice(0, 5);
                          } else if (newStartDate && !editForm.CourtEvent_EndDate) {
                            // Auto-populate end date if it's empty (fallback behavior)
                            newEndDate = newStartDate;
                          }
                          
                          setEditForm({ 
                            ...editForm, 
                            CourtEvent_StartDate: newStartDate,
                            CourtEvent_EndDate: newEndDate,
                            CourtEvent_EndTime: newEndTime
                          });
                        }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        error={editForm.CourtEvent_StartDate.trim() === ''}
                        helperText={editForm.CourtEvent_StartDate.trim() === '' ? 'Start date is required' : ''}
                      />
                      <TextField
                        label="Start Time"
                        type="time"
                        value={editForm.CourtEvent_StartTime}
                        onChange={(e) => {
                          const newStartTime = e.target.value;
                          const newStartDate = editForm.CourtEvent_StartDate;
                          
                          let newEndDate = editForm.CourtEvent_EndDate;
                          let newEndTime = editForm.CourtEvent_EndTime;
                          
                          // If we have both start date and start time, calculate end time as 30 minutes later
                          if (newStartDate && newStartTime) {
                            const startDateTime = new Date(`${newStartDate}T${newStartTime}`);
                            const endDateTime = new Date(startDateTime.getTime() + 30 * 60000); // Add 30 minutes
                            
                            newEndDate = endDateTime.toISOString().split('T')[0];
                            newEndTime = endDateTime.toTimeString().split(' ')[0].slice(0, 5);
                          } else if (newStartTime && !editForm.CourtEvent_EndTime) {
                            // Auto-populate end time if it's empty (fallback: add 30 minutes)
                            const [hours, minutes] = newStartTime.split(':');
                            const totalMinutes = parseInt(hours) * 60 + parseInt(minutes) + 30;
                            const endHour = Math.floor(totalMinutes / 60) % 24;
                            const endMin = totalMinutes % 60;
                            newEndTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
                          }
                          
                          setEditForm({ 
                            ...editForm, 
                            CourtEvent_StartTime: newStartTime,
                            CourtEvent_EndDate: newEndDate,
                            CourtEvent_EndTime: newEndTime
                          });
                        }}
                        slotProps={{htmlInput: { step: 900 }}}
                        inputProps={{ step: 900 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        error={editForm.CourtEvent_StartTime.trim() === ''}
                        helperText={editForm.CourtEvent_StartTime.trim() === '' ? 'Start time is required' : ''}
                      />
                    </Stack>
                    
                    <Typography variant="subtitle1" color="primary">
                      End Date & Time
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="End Date"
                        type="date"
                        value={editForm.CourtEvent_EndDate}
                        onChange={(e) => setEditForm({ ...editForm, CourtEvent_EndDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        error={editForm.CourtEvent_EndDate.trim() === ''}
                        helperText={editForm.CourtEvent_EndDate.trim() === '' ? 'End date is required' : ''}
                      />
                      <TextField
                        label="End Time"
                        type="time"
                        value={editForm.CourtEvent_EndTime}
                        onChange={(e) => setEditForm({ ...editForm, CourtEvent_EndTime: e.target.value })}
                        slotProps={{htmlInput: { step: 900 }}}
                        inputProps={{ step: 900 }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        error={editForm.CourtEvent_EndTime.trim() === ''}
                        helperText={editForm.CourtEvent_EndTime.trim() === '' ? 'End time is required' : ''}
                      />
                    </Stack>
                    
                    <TextField
                      label="Event Title"
                      value={editForm.CourtEvent_Title}
                      onChange={(e) => setEditForm({ ...editForm, CourtEvent_Title: e.target.value })}
                      fullWidth
                      required
                      error={editForm.CourtEvent_Title.trim() === ''}
                      helperText={editForm.CourtEvent_Title.trim() === '' ? 'Title is required' : ''}
                    />
                    
                    <TextField
                      label="Description"
                      value={editForm.CourtEvent_Desc}
                      onChange={(e) => setEditForm({ ...editForm, CourtEvent_Desc: e.target.value })}
                      multiline
                      rows={3}
                      fullWidth
                    />
                    
                    {!isEndTimeAfterStartTime() && (
                      <Alert severity="error">
                        End date and time must be after start date and time.
                      </Alert>
                    )}
                  </Stack>
                </Stack>
              ) : (
                /* View Mode */
                <Box>
                  {/* Court Event Information */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Stack justifyContent={"space-between"} direction="row" alignItems="center" mb={2}>
                        <Typography variant="h6" color="primary" gutterBottom>
                          Event Information
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {!isEditing && selectedEvent && (
                            <>
                              <Tooltip title="Edit Event">
                                <IconButton 
                                  onClick={handleEditEvent}
                                  disabled={saving}
                                  size="small"
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Event">
                                <IconButton 
                                  onClick={handleDeleteConfirm}
                                  disabled={saving}
                                  size="small"
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </Stack>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Title
                          </Typography>
                          <Typography variant="body1">
                            {selectedEvent.CourtEvent_Title || 'No title'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Event ID
                          </Typography>
                          <Typography variant="body1">
                            {selectedEvent.CourtEvent_guid}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Start Time
                          </Typography>
                          <Typography variant="body1">
                            {formatDateTime(selectedEvent.CourtEvent_Start)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            End Time
                          </Typography>
                          <Typography variant="body1">
                            {formatDateTime(selectedEvent.CourtEvent_End)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Description
                          </Typography>
                          <Typography variant="body1">
                            {selectedEvent.CourtEvent_Desc || 'No description'}
                          </Typography>
                        </Box>
                        {/* Lloyd, Aug 10: the answer has to be the same wherever it is read, and
                            the calendar is where staff look when a third party rings about a date. */}
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Does the client need to appear?
                          </Typography>
                          <AppearanceControl
                            courtEventGuid={selectedEvent.CourtEvent_guid}
                            value={selectedEvent.CourtEvent_AppearanceRequirement ?? 'NOT_REQUIRED'}
                            setBy={selectedEvent.CourtEvent_AppearanceSetBy}
                            setAt={selectedEvent.CourtEvent_AppearanceSetAt}
                            isHearing={selectedEvent.CourtEvent_IsHearing ?? true}
                            startEpoch={selectedEvent.CourtEvent_Start}
                            disabled={saving}
                            onChanged={(next) =>
                              setSelectedEvent({
                                ...selectedEvent,
                                CourtEvent_AppearanceRequirement: next,
                              })
                            }
                          />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Ticket Information */}
                  {fullEventData?.ticket && (
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="primary" gutterBottom>
                          Associated Ticket
                        </Typography>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Ticket Number
                            </Typography>
                            <Typography variant="body1">
                              {fullEventData.ticket.Ticket_Number}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Ticket ID
                            </Typography>
                            <Typography variant="body1">
                              {fullEventData.ticket.Ticket_guid}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Citation
                            </Typography>
                            <Typography variant="body1">
                              {fullEventData.ticket.Ticket_Citation}
                            </Typography>
                          </Box>
                          {fullEventData?.user && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">
                                Client Name
                              </Typography>
                              <Typography variant="body1">
                                {fullEventData.user.User_FirstName} {fullEventData.user.User_LastName}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                        {fullEventData?.ticket?.Ticket_guid && (
                          <Button
                            component={Link}
                            to={`/ticket?Ticket_guid=${fullEventData.ticket.Ticket_guid}`}
                            color="primary"
                            variant="outlined"
                            disabled={saving}
                            sx={{ mt: 2, mr: 'auto' }} // Push to the left side
                          >
                            View Ticket
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          {isEditing ? (
            <>
              <Button onClick={handleCancelEdit} disabled={saving}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveEvent} 
                color="primary" 
                variant="contained"
                disabled={saving || !isCourtEventFormValid()}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseModal} color="primary" disabled={saving}>
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Delete Court Event
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this court event? This action cannot be undone.
          </Typography>
          {selectedEvent && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Event to delete:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {selectedEvent.CourtEvent_Title || 'Untitled Event'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(selectedEvent.CourtEvent_Start)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)} 
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteEvent} 
            color="error" 
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete Event'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarEditable;